import {
  createSectionTemplate,
  CreateSectionTemplateArgs,
} from '../../DisasterGPT/hooks/useSectionTemplates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import {
  addSitrepSection,
  keys as SitrepsV2Keys,
  PollSitrepResponse,
} from '../../DisasterGPT/hooks/useSitrepsV2';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

type CreateSectionTemplateAndSectionArgs = {
  vars: CreateSectionTemplateArgs;
  group_guid: UUID;
  user_guid: UUID;
  sitrep: PollSitrepResponse | undefined;
};
const createSectionTemplateAndSection = async ({
  vars,
  group_guid,
  user_guid,
  sitrep,
}: CreateSectionTemplateAndSectionArgs) => {
  const createdTemplate = await createSectionTemplate(vars, group_guid);
  if (!createdTemplate?.id) {
    throw new Error('Section template was created without an id.');
  }
  if (!sitrep) {
    throw new Error('Sitrep not loaded; cannot add the new section.');
  }
  await addSitrepSection(
    {
      sitrepId: sitrep.id,
      sectionTemplateId: createdTemplate.id,
      selectedDatetime: sitrep.timestamp,
      sitrep_source: sitrep.sitrep_source,
      sitrep_source_id: sitrep.sitrep_source_id,
      order: sitrep.sections.length + 1,
      aor_id: sitrep.aor_id ?? '',
    },
    user_guid,
    group_guid
  );
  return {
    templateId: createdTemplate.id,
    sitrepId: sitrep.id,
    type: vars.type,
  };
};

export const useCreateSitrepSectionAndTemplate = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const { sitrepId } = useParams();
  const sitrep = queryClient.getQueryData<PollSitrepResponse>(
    SitrepsV2Keys.byId(sitrepId)
  );

  return useMutation<
    { templateId: string; sitrepId: string; type: string },
    Error,
    CreateSectionTemplateArgs
  >({
    mutationFn: (vars) =>
      createSectionTemplateAndSection({ vars, group_guid, user_guid, sitrep }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: SitrepsV2Keys.byId(result.sitrepId),
      });
      void queryClient.invalidateQueries({
        queryKey: SitrepsV2Keys.allByGroup(group_guid),
      });
      void queryClient.invalidateQueries({ queryKey: SitrepsV2Keys.base });
    },
    onError: (error) => {
      console.error(error);
      toast.error(
        error?.message || 'Failed to create section. Please try again.'
      );
    },
  });
};
